import React from 'react';
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { EmptyState, InlineMessage, LoadingState, StatusChip, statusTone } from './ui';
import { StoreAttestedChip } from './StoreRebateAttestation';
import { listVendorStoreAttestedPrograms, VendorStoreAttestedProgram } from '../services/mapPricingApi';

/**
 * Priya's read-only list of rebates her dealers run on her brands before she is
 * on Motzu (flag store_attested_rebates). No actions: the store decides its own
 * program, and nothing here approves or denies one.
 */
export const VendorStoreRunRebates: React.FC = () => {
  const { t } = useAppTranslation();
  const { data, isLoading, isError } = useQuery<VendorStoreAttestedProgram[]>({
    queryKey: ['vendor-store-attested-rebates'],
    queryFn: listVendorStoreAttestedPrograms,
  });

  const runs = (p: VendorStoreAttestedProgram) =>
    `${p.startAt ? new Date(p.startAt).toLocaleDateString() : '—'} – ${p.endAt ? new Date(p.endAt).toLocaleDateString() : '—'}`;
  const scope = (p: VendorStoreAttestedProgram) =>
    !p.scope || p.scope.kind === 'all'
      ? t('store.attestedRebates.allLocations')
      : p.scope.kind === 'location'
        ? t('store.attestedRebates.oneLocation')
        : t('store.attestedRebates.oneIntegration');
  const status = (p: VendorStoreAttestedProgram) => (
    <StatusChip label={t(`store.attestedRebates.status.${p.status}`)} tone={statusTone(p.status)} size="small" />
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('store.attestedRebates.vendorIntro')}
      </Typography>
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <InlineMessage severity="error">{t('store.attestedRebates.vendorLoadError')}</InlineMessage>
      ) : !data || data.length === 0 ? (
        <EmptyState title={t('store.attestedRebates.vendorTab')} description={t('store.attestedRebates.vendorEmpty')} size="compact" />
      ) : (
        <>
          {/* Phone width: one card per program instead of a wide table. */}
          <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {data.map((p) => (
              <Paper key={p.id} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">{p.organizationName || p.organizationId}</Typography>
                <Typography variant="body2">{p.name}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {runs(p)} · {scope(p)}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  {status(p)}
                  <StoreAttestedChip attestation={p.storeAttestation} />
                </Stack>
              </Paper>
            ))}
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('store.attestedRebates.colStore')}</TableCell>
                  <TableCell>{t('store.attestedRebates.colProgram')}</TableCell>
                  <TableCell>{t('store.attestedRebates.colRuns')}</TableCell>
                  <TableCell>{t('store.attestedRebates.colScope')}</TableCell>
                  <TableCell>{t('store.attestedRebates.colStatus')}</TableCell>
                  <TableCell>{t('store.attestedRebates.colAttestation')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.organizationName || p.organizationId}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{runs(p)}</TableCell>
                    <TableCell>{scope(p)}</TableCell>
                    <TableCell>{status(p)}</TableCell>
                    <TableCell>
                      <StoreAttestedChip attestation={p.storeAttestation} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};
